import { useCallback, useEffect, useRef } from "react";

/**
 * Debounce a save callback: `schedule(value)` arms a timer, rapid calls
 * coalesce to one save with the last value, and `flush()` fires any pending
 * save immediately (also runs on unmount so pending edits aren't lost).
 */
export function useDebouncedSave<T>(save: (value: T) => void, delayMs = 350) {
  const saveRef = useRef(save);
  useEffect(() => {
    saveRef.current = save;
  });

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Wrapped in an object so falsy values of T are still treated as pending.
  const pendingRef = useRef<{ value: T } | null>(null);

  const flush = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (pendingRef.current !== null) {
      const { value } = pendingRef.current;
      pendingRef.current = null;
      saveRef.current(value);
    }
  }, []);

  const schedule = useCallback(
    (value: T) => {
      pendingRef.current = { value };
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(flush, delayMs);
    },
    [delayMs, flush],
  );

  // Flush pending save on unmount (detail-page navigation, row unmount).
  useEffect(() => () => flush(), [flush]);

  return { schedule, flush };
}
